import { MatchRepository, ParticipantRepository, TournamentRepository } from '../repositories';
import { logger } from '@/src/core/utils';

export interface UserStats {
  tournamentsPlayed: number;
  tournamentsWon: number;
  challengesPlayed: number;
  challengesWon: number;
  wins: number;
  losses: number;
  draws: number;
}

export class GetUserStatsUseCase {
  constructor(
    private participantRepository: ParticipantRepository,
    private matchRepository: MatchRepository,
    private tournamentRepository: TournamentRepository
  ) {}

  async execute(userId: string): Promise<UserStats> {
    logger.debug('GetUserStats', 'Calculating stats', { userId });

    // Obtener todas las participaciones del usuario
    const participations = await this.participantRepository.getByUserId(userId);

    let tournamentsPlayed = 0;
    let tournamentsWon = 0;
    let challengesPlayed = 0;
    let challengesWon = 0;
    let totalWins = 0;
    let totalLosses = 0;
    let totalDraws = 0;

    // Helper function to check if tournament is a challenge
    const isChallengeTournament = (tournament: { name: string; maxParticipants?: number }) => {
      return tournament.name.startsWith('Challenge:') && tournament.maxParticipants === 2;
    };

    // Para cada participación, verificar si ganó el torneo y contar matches
    for (const participation of participations) {
      // Verificar si ganó el torneo
      const tournament = await this.tournamentRepository.getById(participation.tournamentId);
      if (!tournament) continue;

      const isChallenge = isChallengeTournament(tournament);

      // Count participation
      if (isChallenge) {
        challengesPlayed++;
      } else {
        tournamentsPlayed++;
      }

      if (tournament.status === 'completed') {
        // Obtener todos los matches del torneo
        const matches = await this.matchRepository.getByTournamentId(participation.tournamentId);

        // Buscar el último match (final) para ver quién ganó
        const finalMatches = matches.filter(m => m.status === 'completed').sort((a, b) => {
          return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
        });

        if (finalMatches.length > 0) {
          const finalMatch = finalMatches[0];
          if (finalMatch.winnerId === participation.id) {
            if (isChallenge) {
              challengesWon++;
            } else {
              tournamentsWon++;
            }
          }
        }
      }

      // Contar victorias, derrotas y empates en matches
      const tournamentMatches = await this.matchRepository.getByTournamentId(participation.tournamentId);

      for (const match of tournamentMatches) {
        if (match.status === 'completed') {
          // Verificar si el participante estuvo en este match
          const isParticipant1 = match.participant1Id === participation.id;
          const isParticipant2 = match.participant2Id === participation.id;

          if (isParticipant1 || isParticipant2) {
            if (match.winnerId === participation.id) {
              totalWins++;
            } else if (match.winnerId === null) {
              totalDraws++;
            } else if (match.winnerId) {
              totalLosses++;
            }
          }
        }
      }
    }

    const stats: UserStats = {
      tournamentsPlayed,
      tournamentsWon,
      challengesPlayed,
      challengesWon,
      wins: totalWins,
      losses: totalLosses,
      draws: totalDraws,
    };

    logger.debug('GetUserStats', 'Stats calculated', { tournamentsPlayed, tournamentsWon, wins: totalWins, losses: totalLosses });

    return stats;
  }
}
